import { useMutation } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { signTypedData } from '@wagmi/core';
import CryptoJS from 'crypto-js';
import { useAccountStore } from '@/store/use-account-store';
import {
  publicApi,
  updatePrivateApiHeaders,
} from '@/lib/api-clients/rest-client';
import { wagmiConfig } from '@/lib/wagmi/config';
import { useUnifiedAccount } from '@/hooks';
import { useWallet } from '@solana/wallet-adapter-react';
import { uint8ArrayToHex } from '../utils';
import { getDefaultMarket } from '@/app/router';
import { useModalStore } from '@/store/use-modal-store';

type RegisterResponse = {
  apiKey: string;
  accGroup: number;
};

type ExtendedRegisterResponse = RegisterResponse & {
  signingKey: `0x${string}`;
};

export function useRegisterUser() {
  const { connector, networkType } = useUnifiedAccount();
  const { signMessage } = useWallet();
  const { setQrCodeUrl } = useModalStore();

  return useMutation({
    mutationFn: async (variables: {
      address: string;
      rememberMe: boolean;
      isMobile?: boolean;
    }): Promise<ExtendedRegisterResponse> => {
      const { address, isMobile } = variables;
      const wallet = ethers.Wallet.createRandom();
      const signingAddr = wallet.address.toLowerCase() as `0x${string}`;
      const expiryTime =
        Math.floor(useAccountStore.getState().getAccurateTime()) +
        1000 * 3600 * 24 * 14;

      const message = {
        domain: {
          name: 'VestRouterV2',
          version: '0.0.1',
          verifyingContract: import.meta.env.VITE_VERIFYING_CONTRACT_1CT,
        },
        primaryType: 'SignerProof' as const,
        types: {
          SignerProof: [
            { name: 'approvedSigner', type: 'address' },
            { name: 'signerExpiry', type: 'uint256' },
          ],
        },
        message: {
          approvedSigner: signingAddr,
          signerExpiry: networkType ? expiryTime : BigInt(expiryTime),
        },
      };

      const encodedMessage = new TextEncoder().encode(
        JSON.stringify({
          approvedSigner: signingAddr,
          signerExpiry: expiryTime,
        }),
      );

      let signature;

      if (!networkType) signature = await signTypedData(wagmiConfig, message);
      else if (networkType && signMessage)
        signature = uint8ArrayToHex(await signMessage(encodedMessage));

      const data = {
        networkType,
        signingAddr,
        primaryAddr: networkType ? address : address.toLowerCase(),
        signature,
        expiryTime, // 2 weeks
        isMobile,
      };

      const res = await publicApi.post('/register', data);
      return {
        ...res,
        signingKey: wallet.privateKey as `0x${string}`,
      };
    },
    onSuccess: (
      res: ExtendedRegisterResponse,
      variables: { address: string; rememberMe: boolean; isMobile?: boolean },
    ) => {
      const { address, rememberMe, isMobile } = variables;
      const { setAuthParams, setHasAuth } = useAccountStore.getState();
      if (isMobile) {
        const secretKey = import.meta.env.VITE_REST_URL; // Should be kept private or fetched securely.

        const dataToEncrypt = {
          address,
          signingKey: res.signingKey,
          apiKey: res.apiKey,
          accountGroup: res.accGroup,
        };

        const encrypted = CryptoJS.AES.encrypt(
          JSON.stringify(dataToEncrypt),
          secretKey,
        ).toString();

        const encodedEncrypted = encodeURIComponent(encrypted);

        const link = `${window.location.origin}/trade/${getDefaultMarket()}?link=${encodedEncrypted}`;
        // console.error(link);
        setQrCodeUrl(link);
      } else {
        setAuthParams(address, {
          address,
          connector: connector?.name,
          signingKey: res.signingKey,
          apiKey: res.apiKey,
          accountGroup: res.accGroup,
          listenKey: '',
          listenKeyExpiry: 0,
          shouldPersist: rememberMe,
        });
        updatePrivateApiHeaders({
          xrestservermm: `restserver${res.accGroup}`,
          'X-API-KEY': res.apiKey,
        });

        setHasAuth(true);
      }
    },
  });
}
